import * as Sentry from "@sentry/vue";
import { isTokenNotValidError } from "@/utils/DjangoError";

type AppErrorOptions = {
  cause?: Error;
  context?: Record<string, string | number | boolean | null | undefined>;
};

export function toError(e: unknown, defaultMessage: string = "Unknown error"): Error {
  const fallback = defaultMessage;

  if (e instanceof Error) return e;
  if (typeof e === "string") return new Error(e);
  return new Error(fallback);
}

/**
 * Logs an error to Sentry for error tracking and monitoring.
 *
 * Automatically normalizes the error and sends it to Sentry with optional context.
 * Available globally (no import needed).
 *
 * @param error - The error to log (any type - will be normalized to Error)
 * @param options.context - Additional metadata for debugging:
 *   - `feature`: Feature/module name (e.g., "contract-form", "file-upload")
 *   - `step`: Action being performed (e.g., "submit", "update")
 *   - Entity IDs: `patientId`, `userId`, `treatmentPlanId`, etc.
 *   - HTTP details: `url`, `method`, `status` (for API errors)
 * @param logAppError - Log AppError instances (default: false). AppErrors are already logged on creation.
 *
 * @example
 * catch (error) {
 *   logError(error, {
 *     context: { feature: "contract-form", step: "update" }
 *   });
 * }
 */
export function logError(
  error: unknown,
  options?: AppErrorOptions,
  logAppError: boolean = false
): void {
  if (isTokenNotValidError(error)) {
    return;
  }

  const normalizedError = toError(error);
  if (normalizedError instanceof AppError && !logAppError) {
    return;
  }

  const extra = { ...(options?.context ?? {}) };

  const cause = normalizedError.cause;
  if (cause instanceof Error) {
    extra.causeName = cause.name;
    extra.causeMessage = cause.message;
    if (cause.stack) {
      extra.causeStack = cause.stack;
    }
  }

  Sentry.captureException(normalizedError, { extra });
}

declare global {
  var logError: (error: unknown, options?: AppErrorOptions, logAppError?: boolean) => void;
}

export class AppError extends Error {
  cause?: Error;

  constructor(message: string, options?: AppErrorOptions) {
    super(message, { cause: options?.cause });
    this.name = "AppError";
    this.cause = options?.cause;

    logError(this, options, true);
  }
}

globalThis.logError = logError;
